import {
  CallMetrics,
  ChartParam,
  DashboardFilters,
} from '@/services/dashboard-service/dashboard.service.types';
import { calculateSecondsToMinutes } from '@/utils/helpers';
import styles from './call-metric.module.scss';
import dayjs from 'dayjs';

export const calculateDateForXAxis = (
  time_range: DashboardFilters['time_range'],
  date: CallMetrics['chart'][0]['date'],
  start_time?: DashboardFilters['start_time'],
  end_time?: DashboardFilters['end_time']
) => {
  const parsedDate = dayjs(date);
  const startDate = start_time ? dayjs(start_time) : null;
  const endDate = end_time ? dayjs(end_time) : null;
  const isSingleOrTwoDayRange = startDate && endDate && endDate.diff(startDate, 'days') <= 1;

  if (time_range === '24h' || isSingleOrTwoDayRange) {
    return parsedDate.format('h A');
  }

  return parsedDate.format('D MMM');
};

export const createCallMetricCardsData = (callMetrics?: CallMetrics) => {
  if (!callMetrics) {
    return [];
  }

  const getArrowColorClass = (
    change: number,
    isReverse = false,
    isAbandon = false,
    isAnswerRate = false
  ) => {
    if ((isAbandon || isAnswerRate) && change === 0) {
      return styles.neutral;
    }
    return !isReverse
      ? change > 0
        ? styles.increase
        : styles.decrease
      : change > 0
        ? styles.decrease
        : styles.increase;
  };

  return [
    {
      title: 'Answer Rate',
      tooltipContent: 'Percentage of incoming calls that were successfully answered.',
      currentPercent: `${Math.round(callMetrics.answer.current_percent)}%`,
      change: callMetrics.answer.change,
      calls: String(callMetrics.answer.value),
      description: 'calls answered',
      arrowColor: getArrowColorClass(callMetrics.answer.change, false, false, true),
      chartParam: 'answer' as ChartParam,
    },
    {
      title: 'Abandon Rate',
      tooltipContent:
        'Percentage of incoming calls that were abandoned by the caller before being answered.',
      currentPercent: `${Math.round(callMetrics.abandon.current_percent)}%`,
      change: callMetrics.abandon.change,
      calls: String(callMetrics.abandon.value),
      description: 'calls abandoned',
      arrowColor: getArrowColorClass(callMetrics.abandon.change, true, true),
      chartParam: 'abandon' as ChartParam,
    },
    {
      title: 'AI Resolution Rate',
      tooltipContent:
        'Percentage of calls that were resolved by the AI without needing to be transferred to a human agent.',
      currentPercent: `${Math.round(callMetrics.ai_resolution.current_percent)}%`,
      change: callMetrics.ai_resolution.change,
      calls: String(callMetrics.ai_resolution.value),
      description: 'calls resolved by AI',
      arrowColor: getArrowColorClass(callMetrics.ai_resolution.change),
      chartParam: 'ai_resolution' as ChartParam,
    },
    {
      title: 'Agent Handoff Rate',
      tooltipContent: 'Percentage of calls that were transferred from AI to human agents.',
      currentPercent: `${Math.round(callMetrics.agent_handoff.current_percent)}%`,
      change: callMetrics.agent_handoff.change,
      calls: String(callMetrics.agent_handoff.value),
      description: 'calls handed off to agents',
      arrowColor: getArrowColorClass(callMetrics.agent_handoff.change, true),
      chartParam: 'agent_handoff' as ChartParam,
    },
    {
      title: 'Average Speed of Answer (ASA)',
      tooltipContent:
        'Average time (in minutes) it takes for a call to be answered by an AI or agent.',
      currentPercent: calculateSecondsToMinutes(callMetrics.asa.value),
      change: callMetrics.asa.change,
      calls: '',
      description: '(minutes:seconds)',
      arrowColor: getArrowColorClass(callMetrics.asa.change, true),
      chartParam: 'asa' as ChartParam,
    },
  ];
};
